import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, LogOut, Upload, Lock } from "lucide-react";
import Seo from "@/components/Seo";
import AdminSkal from "@/components/AdminSkal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Viaggi", "Il mio percorso", "Personal Brand"];
const TOKEN_KEY = "vig_admin_token";

const emptyForm = {
  title: "",
  category: CATEGORIES[0],
  date: new Date().toISOString().slice(0, 10),
  cover_image: "",
  cover_alt: "",
  excerpt: "",
  content_text: "",
  meta_title: "",
  meta_description: "",
};

export default function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("blog");

  const headers = { Authorization: `Bearer ${token}` };

  const loadPosts = () => {
    axios
      .get(`${API}/admin/posts`, { headers })
      .then((r) => setPosts(r.data))
      .catch((e) => {
        if (e?.response?.status === 401) logout();
      });
  };

  useEffect(() => {
    if (token) loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  const onLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
      toast.success("Benvenuta Laure!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Accesso non riuscito.");
    } finally {
      setLoggingIn(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, { headers });
      setForm((f) => ({ ...f, cover_image: data.url }));
      toast.success("Immagine caricata.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Caricamento non riuscito.");
    } finally {
      setUploading(false);
    }
  };

  const onCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/posts`, form, { headers });
      toast.success(`Articolo pubblicato: /blog/${data.slug}`);
      setForm(emptyForm);
      loadPosts();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Controlla i campi e riprova.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (post) => {
    if (!window.confirm(`Eliminare "${post.title}"? L'azione non si può annullare.`)) return;
    try {
      await axios.delete(`${API}/admin/posts/${post.id}`, { headers });
      toast.success("Articolo eliminato.");
      loadPosts();
    } catch {
      toast.error("Eliminazione non riuscita.");
    }
  };

  if (!token) {
    return (
      <div data-testid="admin-login" className="min-h-[80vh] flex items-center justify-center px-6 pt-24 pb-16">
        <Seo title="Area riservata | Laure Hila" description="Area riservata." robots="noindex, nofollow" />
        <form
          onSubmit={onLogin}
          className="w-full max-w-sm p-8 rounded-2xl bg-sand/70 border border-line"
        >
          <div className="w-12 h-12 rounded-full bg-ink text-gold flex items-center justify-center mb-5">
            <Lock size={20} />
          </div>
          <h1 className="font-serif text-3xl text-ink">Area riservata</h1>
          <p className="mt-2 text-sm text-stone">Accedi per gestire blog e candidature Skål Albania.</p>
          <input
            type="password"
            data-testid="admin-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
            className="mt-6 w-full px-4 py-3.5 rounded-lg bg-cream border border-line focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20 text-ink transition-[border-color,box-shadow] duration-300"
          />
          <button
            type="submit"
            data-testid="admin-login-button"
            disabled={loggingIn}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-ink text-cream font-semibold text-sm hover:bg-gold disabled:opacity-60 transition-colors duration-300"
          >
            {loggingIn ? <Loader2 size={16} className="animate-spin" /> : null}
            Accedi
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="pt-28 md:pt-36 pb-24">
      <Seo title="Gestione blog | Laure Hila" description="Area riservata." robots="noindex, nofollow" />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between gap-4 mb-8">
          <div>
            <p className="eyebrow mb-2">Area riservata</p>
            <h1 className="font-serif text-4xl md:text-5xl text-ink">Gestione sito</h1>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 text-sm font-semibold text-stone hover:text-gold transition-colors duration-300"
          >
            <LogOut size={16} />
            Esci
          </button>
        </div>

        <div className="flex gap-2.5 mb-10" role="tablist" aria-label="Sezioni area riservata">
          {[
            { key: "blog", label: "Blog" },
            { key: "skal", label: "Candidature Skål" },
          ].map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold tracking-wide transition-colors duration-300 ${
                tab === t.key
                  ? "bg-ink text-cream"
                  : "bg-sand text-stone hover:text-gold border border-line"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "skal" ? (
          <AdminSkal headers={headers} />
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <form
            onSubmit={onCreate}
            data-testid="admin-post-form"
            className="lg:col-span-7 p-7 md:p-9 rounded-2xl bg-sand/70 border border-line h-fit"
          >
            <h2 className="font-serif text-2xl text-ink">Nuovo articolo</h2>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-stone mb-2">Titolo</label>
                <input
                  data-testid="admin-title-input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  placeholder="Titolo dell'articolo"
                  className="w-full px-4 py-3 rounded-lg bg-cream border border-line focus:border-gold focus:outline-none text-ink"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-stone mb-2">Categoria</label>
                <select
                  data-testid="admin-category-select"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-cream border border-line focus:border-gold focus:outline-none text-ink"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-stone mb-2">Data</label>
                <input
                  type="date"
                  data-testid="admin-date-input"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-cream border border-line focus:border-gold focus:outline-none text-ink"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-stone mb-2">Immagine di copertina</label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <label
                    data-testid="admin-upload-button"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-ink text-cream text-sm font-semibold cursor-pointer hover:bg-gold transition-colors duration-300"
                  >
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    Carica foto
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onUpload} />
                  </label>
                  <input
                    data-testid="admin-cover-input"
                    value={form.cover_image}
                    onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                    required
                    placeholder="oppure incolla l'URL dell'immagine"
                    className="flex-1 px-4 py-3 rounded-lg bg-cream border border-line focus:border-gold focus:outline-none text-ink text-sm"
                  />
                </div>
                {form.cover_image && (
                  <img src={form.cover_image} alt="Anteprima copertina" className="mt-3 w-40 aspect-[4/3] object-cover rounded-lg" />
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-stone mb-2">Descrizione immagine (alt text)</label>
                <input
                  data-testid="admin-alt-input"
                  value={form.cover_alt}
                  onChange={(e) => setForm({ ...form, cover_alt: e.target.value })}
                  required
                  placeholder="Es. Laure Hila a Santorini con vista sul mare"
                  className="w-full px-4 py-3 rounded-lg bg-cream border border-line focus:border-gold focus:outline-none text-ink"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-stone mb-2">Testo dell'articolo</label>
                <textarea
                  data-testid="admin-content-input"
                  value={form.content_text}
                  onChange={(e) => setForm({ ...form, content_text: e.target.value })}
                  required
                  rows={12}
                  placeholder={"Scrivi qui l'articolo.\n\nSepara i paragrafi con una riga vuota.\n\n## Per un sottotitolo inizia la riga con due cancelletti\n\n> Per una citazione inizia la riga con il segno maggiore"}
                  className="w-full px-4 py-3 rounded-lg bg-cream border border-line focus:border-gold focus:outline-none text-ink text-sm leading-relaxed resize-y"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-stone mb-2">Anteprima / riassunto (facoltativo)</label>
                <textarea
                  data-testid="admin-excerpt-input"
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  rows={2}
                  placeholder="Se vuoto, viene creato automaticamente dal primo paragrafo"
                  className="w-full px-4 py-3 rounded-lg bg-cream border border-line focus:border-gold focus:outline-none text-ink text-sm resize-y"
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="admin-publish-button"
              disabled={saving}
              className="mt-7 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-ink text-cream font-semibold text-sm hover:bg-gold disabled:opacity-60 transition-colors duration-300"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : null}
              Pubblica articolo
            </button>
          </form>

          <div className="lg:col-span-5">
            <h2 className="font-serif text-2xl text-ink mb-5">Articoli pubblicati ({posts.length})</h2>
            <div className="flex flex-col gap-4">
              {posts.map((p) => (
                <div
                  key={p.id}
                  data-testid={`admin-post-${p.slug}`}
                  className="flex gap-4 p-4 rounded-xl border border-line bg-cream"
                >
                  <img
                    src={p.cover_image}
                    alt={p.cover_alt}
                    className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-gold font-semibold">{p.category}</p>
                    <p className="font-serif text-lg leading-snug text-ink truncate">{p.title}</p>
                    <p className="text-xs text-stone mt-1">{p.date}</p>
                  </div>
                  <button
                    onClick={() => onDelete(p)}
                    data-testid={`admin-delete-${p.slug}`}
                    aria-label={`Elimina ${p.title}`}
                    className="self-start p-2 text-stone hover:text-red-700 transition-colors duration-300"
                  >
                    <Trash2 size={17} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
